import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const navLinks = [
  { label: "Features", href: "/#capabilities" },
  {
    label: "Solutions",
    href: "/solutions",
    children: [
      { label: "Meta Ads", href: "/solutions/meta-ads" },
      { label: "Google Ads", href: "/solutions/google-ads" },
      { label: "Competitor Pricing", href: "/solutions/competitor-pricing" },
    ],
  },
  { label: "Pricing", href: "/#pricing" },
  {
    label: "Tools",
    href: "/tools/meta-ads-search",
    children: [
      { label: "Meta Ads Search", href: "/tools/meta-ads-search" },
      { label: "Google Ads Search", href: "/tools/google-ads-search" },
    ],
  },
  { label: "Blog", href: "/blog" },
];

export default function MarketingNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={cn(
          "fixed top-0 z-50 w-full border-b transition-colors duration-300",
          scrolled
            ? "border-white/10 bg-[rgba(11,12,15,0.85)] backdrop-blur-xl"
            : "border-transparent bg-transparent backdrop-blur-sm",
        )}
      >
        <div className="mx-auto flex h-16 max-w-[1200px] items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2.5" data-testid="site-logo">
            <span className="text-lg font-display font-bold tracking-tight text-foreground">RADAR</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => link.children && setHoveredDropdown(link.label)}
                onMouseLeave={() => setHoveredDropdown(null)}
              >
                <Link
                  to={link.href}
                  className={cn(
                    "flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors rounded-lg hover:bg-white/[0.04]",
                    location.pathname === link.href && "text-foreground",
                  )}
                >
                  {link.label}
                  {link.children ? <ChevronDown className="h-3.5 w-3.5 opacity-50" /> : null}
                </Link>
                <AnimatePresence>
                  {link.children && hoveredDropdown === link.label ? (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 mt-1 w-52 rounded-xl border border-white/10 bg-[#12141B] p-2 shadow-2xl backdrop-blur-xl"
                    >
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block rounded-lg px-3 py-2.5 text-sm text-foreground/70 hover:text-foreground hover:bg-white/[0.04] transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
              data-testid="login-link"
            >
              Log in
            </Link>
            <Link
              to="/waitlist"
              className="inline-flex items-center px-5 py-2.5 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors shadow-[0_0_20px_rgba(204,255,108,0.15)]"
              data-testid="primary-cta-button"
            >
              Get $99 Pro for $49
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-foreground/70 hover:text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen ? (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 bg-[#0B0C0F]/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-nav-sheet"
          >
            <div className="flex flex-col gap-1 px-6 pt-24 pb-8">
              {navLinks.map((link) => (
                <div key={link.label}>
                  <Link
                    to={link.href}
                    className="block py-3 text-lg font-medium text-foreground/80 hover:text-foreground transition-colors border-b border-white/5"
                  >
                    {link.label}
                  </Link>
                  {link.children ? (
                    <div className="pl-4">
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block py-2 text-sm text-foreground/60 hover:text-foreground transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
              <div className="mt-8 flex flex-col gap-3">
                <Link to="/login" className="text-center py-3 text-sm font-medium text-foreground/70 border border-white/10 rounded-xl">
                  Log in
                </Link>
                <Link to="/waitlist" className="text-center py-3 text-sm font-semibold bg-[#CCFF6C] text-black rounded-xl">
                  Get $99 Pro for $49
                </Link>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}
